import React, { useState } from 'react';
import { Card, Form, Input, Button, Alert, Typography, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { authService } from '../../services/authService';

const { Title, Paragraph } = Typography;

// Mirrors the auth-service password policy (ChangePasswordDto).
const PASSWORD_RULES = [
  { required: true, message: 'Enter a new password' },
  { min: 8, message: 'At least 8 characters' },
  {
    pattern: /((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/,
    message: 'Use upper + lower case and a number or symbol',
  },
];

/** Protected page: the signed-in operator changes their own password. */
const ChangePasswordPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFinish = async (values: { currentPassword: string; newPassword: string }) => {
    setLoading(true);
    setError(null);
    try {
      await authService.changePassword(values.currentPassword, values.newPassword);
      message.success('Password changed successfully');
      form.resetFields();
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          'Could not change your password. Check your current password and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 480 }}>
      <Title level={3}>Change password</Title>
      <Paragraph type="secondary">Update the password for your operator account.</Paragraph>
      <Card>
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
        <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
          <Form.Item
            name="currentPassword"
            label="Current password"
            rules={[{ required: true, message: 'Enter your current password' }]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
          </Form.Item>

          <Form.Item name="newPassword" label="New password" rules={PASSWORD_RULES} hasFeedback>
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            name="confirm"
            label="Confirm new password"
            dependencies={['newPassword']}
            hasFeedback
            rules={[
              { required: true, message: 'Confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  return !value || getFieldValue('newPassword') === value
                    ? Promise.resolve()
                    : Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={loading}>
              Update password
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ChangePasswordPage;
